import { useEffect, useState } from 'react';
import { sharedDb } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { searchProduct } from '@/lib/gemini';
import type { Product } from '@/types';

export function ProductsPage() {
  const { tenantId } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [searchBusy, setSearchBusy] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    (async () => {
      const { data } = await sharedDb()
        .from('products')
        .select('*')
        .order('manufacturer')
        .order('product_code')
        .limit(200);
      setProducts((data ?? []) as unknown as Product[]);
      setLoading(false);
    })();
  }, [tenantId]);

  const filtered = q
    ? products.filter(
        (p) =>
          p.product_code.includes(q) ||
          p.manufacturer.includes(q) ||
          (p.product_name ?? '').includes(q),
      )
    : products;

  const handleWebSearch = async () => {
    if (!tenantId || !q.trim()) return;
    setSearchBusy(true);
    try {
      const r = await searchProduct({ tenantId, query: q.trim() });
      // 結果を表示して、確認後にマスター追加
      const ok = confirm(
        `次の商品をマスターに追加しますか?\n\n${r.manufacturer} / ${r.product_code}\n${r.product_name}${r.is_discontinued ? '\n⚠️ 廃番' : ''}${r.successor_code ? `\n後継: ${r.successor_code}` : ''}`,
      );
      if (ok) {
        await sharedDb()
          .from('products')
          .insert({
            tenant_id: tenantId,
            manufacturer: r.manufacturer,
            product_code: r.product_code,
            product_name: r.product_name,
            category: r.category,
            size_text: r.size_text,
            is_discontinued: r.is_discontinued,
            predecessor_code: r.predecessor_code,
            successor_code: r.successor_code,
            catalog_url: r.catalog_url,
          });
        location.reload();
      }
    } catch (e) {
      alert(`検索エラー: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSearchBusy(false);
    }
  };

  return (
    <div>
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <h1 className="text-sm font-semibold">商品マスター</h1>
        <div className="mt-2 flex gap-2">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="商品コード / メーカー / 商品名"
            className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
          />
          <button
            type="button"
            disabled={searchBusy || !q.trim()}
            onClick={handleWebSearch}
            className="shrink-0 rounded-md bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
          >
            {searchBusy ? '検索中' : 'Web検索'}
          </button>
        </div>
      </header>
      {loading ? (
        <div className="p-6 text-sm text-slate-500">読み込み中...</div>
      ) : filtered.length === 0 ? (
        <div className="p-6 text-sm text-slate-500">
          見つかりません。「Web検索」で新規商品を追加できます。
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {filtered.map((p) => (
            <li key={p.id} className="px-4 py-3">
              <div className="text-sm">
                <span className="text-slate-500">{p.manufacturer}</span>
                {' / '}
                <span className="font-mono font-medium">{p.product_code}</span>
              </div>
              <div className="mt-0.5 text-[10px] text-slate-500">
                {p.product_name && <span>{p.product_name}</span>}
                {p.is_discontinued && <span className="ml-2 text-rose-700">廃番</span>}
                {p.successor_code && <span className="ml-2">→ {p.successor_code}</span>}
              </div>
              {p.catalog_url && (
                <a href={p.catalog_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-[10px] text-emerald-700 underline">
                  カタログ →
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
